import React from "react";
import { Table, Tag, Alert, Space, Tooltip } from "antd";
import {
  CalculatorOutlined, PrinterOutlined, CheckCircleOutlined, RollbackOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { ButtonItem } from "../../../../../Components/Items";
import {
  fmtMXN, fechaTxt, filaIncluida, isrVigente, redondear2, diasProporcionales, fmtDias,
} from "../aguinaldoUtils";
import DetalleCalculoAguinaldo from "../DetalleCalculoAguinaldo";

/**
 * Paso 5 — resumen. En borrador: calcular el ISR (simulación del motor, sin tocar la nómina),
 * imprimir la hoja de trabajo y aplicar. Aplicado: el resultado por empleado, imprimir y deshacer
 * (mientras nada esté timbrado).
 */
const PasoAplicar = ({
  ejercicio, fechaCorte, diasAnio, tipoAplicacion, destinoDescripcion, empleados, estatus, fechaAplicacion,
  nombrePrenomina, simulando, aplicando, deshaciendo,
  onCalcularIsr, onImprimir, onAplicar, onDeshacer,
}) => {
  const aplicado = estatus === "aplicado";
  const filas = aplicado
    ? (empleados || []).filter((e) => e.aplicado || e.mensaje)
    : (empleados || []).filter(filaIncluida);
  const pagados = aplicado ? filas.filter((e) => e.aplicado) : filas;

  const total = redondear2(pagados.reduce((acc, e) => acc + (Number(e.importe) || 0), 0));
  const conIsr = pagados.filter(isrVigente);
  const isrCompleto = pagados.length > 0 && conIsr.length === pagados.length;
  const isrTotal = redondear2(conIsr.reduce((acc, e) => acc + (e.simulacion?.isr || 0), 0));
  const calculadoEn = conIsr.reduce((max, e) => {
    const d = e.simulacion?.calculadoEn;
    return d && (!max || moment(d).isAfter(max)) ? d : max;
  }, null);

  const dinero = (e, campo) => (isrVigente(e) ? fmtMXN(e.simulacion[campo]) : <span className="aguinaldo-tenue">—</span>);

  const columnas = [
    {
      title: "Empleado",
      key: "empleado",
      render: (_, e) => <span className="aguinaldo-fuerte">{e.nombre}</span>,
    },
    ...(tipoAplicacion === "ordinaria"
      ? [{ title: "Prenómina", key: "prenomina", width: 220, ellipsis: true, render: (_, e) => nombrePrenomina(e.prenominaId) }]
      : []),
    { title: "Días aguin.", dataIndex: "diasAguinaldo", width: 80, align: "center" },
    { title: "Días trab.", dataIndex: "diasTrabajados", width: 80, align: "center" },
    {
      title: "Días a pagar",
      key: "diasPagar",
      width: 96,
      align: "center",
      render: (_, e) => (
        <Tooltip title={`${e.diasAguinaldo} × ${e.diasTrabajados} ÷ ${diasAnio}`}>
          {fmtDias(diasProporcionales(e.diasAguinaldo, diasAnio, e.diasTrabajados))}
        </Tooltip>
      ),
    },
    { title: "Salario diario", dataIndex: "salarioDiario", width: 110, align: "right", render: (n) => fmtMXN(n) },
    {
      title: "Aguinaldo",
      dataIndex: "importe",
      width: 120,
      align: "right",
      render: (n, e) => (e.importeManual
        ? <Tooltip title="Importe capturado a mano"><span className="aguinaldo-aviso-texto">{fmtMXN(n)}</span></Tooltip>
        : fmtMXN(n)),
    },
    { title: "Exento", key: "exento", width: 110, align: "right", render: (_, e) => dinero(e, "exento") },
    { title: "Gravado", key: "gravado", width: 110, align: "right", render: (_, e) => dinero(e, "gravado") },
    { title: "ISR", key: "isr", width: 110, align: "right", render: (_, e) => dinero(e, "isr") },
    { title: "Neto", key: "neto", width: 120, align: "right", render: (_, e) => dinero(e, "neto") },
    {
      title: "Estado",
      key: "estado",
      width: 200,
      render: (_, e) => {
        if (aplicado) {
          return e.aplicado
            ? <Tag color="green">Aplicado</Tag>
            : <Tooltip title={e.mensaje}><Tag color="red">No aplicado</Tag><span className="aguinaldo-tenue">{e.mensaje}</span></Tooltip>;
        }
        if (e.simulacion?.calculadoEn && !isrVigente(e)) return <Tag color="orange">ISR desactualizado</Tag>;
        if (!e.simulacion?.calculadoEn) return <span className="aguinaldo-tenue">Sin ISR calculado</span>;
        return null;
      },
    },
  ];

  return (
    <div className="aguinaldo-paso">
      <div className="aguinaldo-titulo">Resumen</div>

      <div className="aguinaldo-cifras">
        <div className="aguinaldo-cifra"><span>Ejercicio</span><b>{ejercicio}</b></div>
        <div className="aguinaldo-cifra"><span>Corte</span><b>{fechaTxt(fechaCorte)}</b></div>
        <div className="aguinaldo-cifra aguinaldo-cifra-ancha">
          <span>{tipoAplicacion === "extraordinaria" ? "Periodo extraordinario" : "Prenómina ordinaria"}</span>
          <b>{destinoDescripcion}</b>
        </div>
        <div className="aguinaldo-cifra"><span>Empleados</span><b>{pagados.length}</b></div>
        <div className="aguinaldo-cifra"><span>Aguinaldo</span><b>{fmtMXN(total)}</b></div>
        <div className="aguinaldo-cifra">
          <span>ISR {aplicado ? "(simulado)" : ""}</span>
          <b>{isrCompleto ? fmtMXN(isrTotal) : "—"}</b>
        </div>
        <div className="aguinaldo-cifra">
          <span>Neto</span>
          <b>{isrCompleto ? fmtMXN(total - isrTotal) : "—"}</b>
        </div>
      </div>

      {aplicado ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Aplicado el ${fechaAplicacion ? moment(fechaAplicacion).format("DD/MM/YYYY HH:mm") : ""}`}
          description="El aguinaldo ya está en los recibos y la prenómina se recalculó en segundo plano. Revísala y tímbrala desde PreNómina. Mientras no se timbre, puedes deshacerlo aquí."
        />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={calculadoEn
            ? `ISR calculado el ${moment(calculadoEn).format("DD/MM/YYYY HH:mm")}${isrCompleto ? "" : " — hay empleados sin ISR vigente"}`
            : "El ISR todavía no se ha calculado."}
          description="El ISR se simula con el mismo motor de la nómina (tasa efectiva del Art. 174) sin guardar nada en la prenómina. Aplicar escribe el aguinaldo en los recibos y recalcula la prenómina."
        />
      )}

      <Space wrap style={{ marginBottom: 12 }}>
        {!aplicado && (
          <ButtonItem icon={<CalculatorOutlined />} loading={simulando} onClick={onCalcularIsr}>
            Calcular ISR
          </ButtonItem>
        )}
        <ButtonItem icon={<PrinterOutlined />} onClick={onImprimir} loading={!aplicado && simulando}>
          {aplicado ? "Imprimir / Excel" : "Imprimir borrador / Excel"}
        </ButtonItem>
        {!aplicado && (
          <ButtonItem type="primary" icon={<CheckCircleOutlined />} loading={aplicando} onClick={onAplicar}>
            Aplicar aguinaldo
          </ButtonItem>
        )}
        {aplicado && (
          <ButtonItem danger icon={<RollbackOutlined />} loading={deshaciendo} onClick={onDeshacer}>
            Deshacer
          </ButtonItem>
        )}
      </Space>

      <div className="aguinaldo-formula" style={{ marginBottom: 8 }}>
        Días a pagar = días de aguinaldo × días trabajados ÷ <b>{diasAnio}</b> días del año
        <span className="aguinaldo-formula-sep">·</span>
        Aguinaldo = días a pagar × salario diario
      </div>

      <Table
        rowKey={(e) => String(e.empleadoId)}
        size="small"
        columns={columnas}
        dataSource={filas}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
        scroll={{ x: 1400 }}
        expandable={{
          expandedRowRender: (e) => (
            <DetalleCalculoAguinaldo e={e} diasAnio={diasAnio} ejercicio={ejercicio} fechaCorte={fechaCorte} />
          ),
        }}
      />
    </div>
  );
};

export default PasoAplicar;
